import { Box, Button, Divider, Flex, Group, Text, UnstyledButton } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { get } from 'lodash';
import moment from 'moment';

import { deleteIcon } from '@assets';
import { AsyncAutocomplete, DatePickerInput, MultiLangCodes, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';

interface InputConfig {
  type: 'Autocomplete' | 'MultiLang' | 'Input' | 'DatePicker';
  name: string;
  inputName?: string;
  code?: string;
  label: string;
  width?: number;
  search?: (searchText: string) => Promise<{ value: string; label: string }[]>;
  displayCondition?: (row) => boolean;
  'data-pw'?: string;
  excludeList?: string[];
}

interface TransitDuplicateRowProps {
  form: UseFormReturnType<any>;
  inputs: InputConfig[];
  arrayPath: string;
  addButtonLabel: string;
  addButtonDataPW: string;
  inputsPerRow?: number;
  disabled?: boolean;
  maxRows?: number;
  cleanupAdditionalData?: (row) => void;
}

const constants = {
  DEFAULT_INPUTS_PER_ROW: 3,
  DEFAULT_WIDTH: 100,
  DEFAULT_MAX_ROWS: 9999,
  SEPARATOR_COLUMN_COUNT: 2,
  SEPARATOR_WIDTH_DEFAULT: 74,
  SEPARATOR_WIDTH_TWO_COLUMNS: 49,
};

// todo: deprecated, use EditArray from @components instead
export const TransitDuplicateRow: React.FC<TransitDuplicateRowProps> = ({
  form,
  inputs,
  arrayPath,
  addButtonLabel,
  addButtonDataPW,
  inputsPerRow = constants.DEFAULT_INPUTS_PER_ROW,
  disabled = false,
  maxRows = constants.DEFAULT_MAX_ROWS,
  cleanupAdditionalData,
}) => {
  const codelistSystems = useCodeListSystem({ isTransit: true });

  const addRow = () => {
    const rows = [...form.getInputProps(arrayPath).value];
    form.setFieldValue(arrayPath, [...rows, {}]);
  };

  const removeRow = (rowIdx: number) => {
    const rows = [...form.getInputProps(arrayPath).value];
    const value = rows.splice(rowIdx, 1)[0];
    form.setFieldValue(arrayPath, rows);
    if (cleanupAdditionalData) {
      cleanupAdditionalData(value);
    }
  };

  const chunkInputs = (inputList: InputConfig[], chunkSize: number) => {
    const chunks = [];
    for (let i = 0; i < inputList.length; i += chunkSize) {
      chunks.push(inputList.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const renderInputField = (inputConfig: InputConfig, rowIdx: number, inputConfigIdx: number) => {
    const itemValue = form.getInputProps(`${arrayPath}.${rowIdx}`)?.value ?? '';
    if (inputConfig.displayCondition?.(itemValue) === false) {
      return;
    }

    const inputName = inputConfig.inputName ?? `${arrayPath}.${rowIdx}.${inputConfig.name}`;

    const commonInputAttributes = {
      label: `${inputConfig.label} ${rowIdx + 1}`,
      disabled,
      mb: 12,
    };

    return (
      <Box w={`${inputConfig.width ?? constants.DEFAULT_WIDTH}%`} key={inputConfigIdx}>
        {inputConfig.type === 'MultiLang' && (
          <MultiLangCodes
            {...form.getInputProps(inputName)}
            system={codelistSystems}
            code={inputConfig.code}
            excludeList={inputConfig.excludeList}
            {...commonInputAttributes}
            data-pw={`${inputConfig['data-pw']}-${rowIdx}`}
          />
        )}
        {inputConfig.type === 'Autocomplete' && (
          <AsyncAutocomplete {...form.getInputProps(inputName)} fetchOptions={inputConfig.search} {...commonInputAttributes} />
        )}
        {inputConfig.type === 'DatePicker' && (
          <DatePickerInput
            {...form.getInputProps(inputName)}
            value={form.getInputProps(inputName).value ? new Date(form.getInputProps(inputName).value) : null}
            onChange={(value) => form.setFieldValue(inputName, `${moment(value).format('YYYY-MM-DD')}T00:00:00`)}
            {...commonInputAttributes}
          />
        )}
        {inputConfig.type === 'Input' && (
          <TextInput {...form.getInputProps(inputName)} {...commonInputAttributes} data-pw={`${inputConfig['data-pw']}-${rowIdx}`} />
        )}
        {get(form.errors, inputName) && (
          <Text c={'red'} fz={12} pos={'absolute'}>
            {get(form.errors, inputName)}
          </Text>
        )}
      </Box>
    );
  };

  const renderInputs = (inputList: InputConfig[], rowIdx: number) => {
    const inputChunks = chunkInputs(inputList, inputsPerRow);

    return (
      <>
        {inputChunks.map((chunk, chunkIdx) => (
          <Group align="top" key={chunkIdx}>
            {chunk.map((inputConfig, inputConfigIdx) => renderInputField(inputConfig, rowIdx, inputConfigIdx))}
            {chunkIdx === 0 && !disabled && (
              <UnstyledButton ml={10} w={50} h={50} type="button" onClick={() => removeRow(rowIdx)}>
                <img src={deleteIcon} alt="Remove" />
              </UnstyledButton>
            )}
          </Group>
        ))}
      </>
    );
  };

  const arrayPathValue = form.getInputProps(arrayPath).value ?? [];

  return (
    <>
      {arrayPathValue.map((_, rowIdx) => (
        <div key={`${arrayPathValue.length}-${rowIdx}`}>
          {renderInputs(inputs, rowIdx)}
          {rowIdx < arrayPathValue.length - 1 && (
            <Divider
              mt={16}
              mb={8}
              size={0}
              w={`${inputs.length === constants.SEPARATOR_COLUMN_COUNT ? constants.SEPARATOR_WIDTH_TWO_COLUMNS : constants.SEPARATOR_WIDTH_DEFAULT}%`}
              style={{ borderBottom: '1px solid #ccc' }}
            />
          )}
        </div>
      ))}
      {!disabled && maxRows > arrayPathValue.length && (
        <Flex mb={15} justify={'flex-start'}>
          <Button type="button" onClick={addRow} variant="outline" size="lg" data-pw={addButtonDataPW}>
            {addButtonLabel}
          </Button>
        </Flex>
      )}
    </>
  );
};
